/**
 * The workspace profile write path used by `/studio/settings`.
 *
 * Reported defect: the Profile "Edit" button (settings-page.tsx:23) had no
 * `onClick` at all. It rendered as a normal, pressable outline button and did
 * nothing when pressed — no editor, no error, no state change.
 *
 * `full_name` is real, load-bearing data: the Settings header and the workspace
 * domain line are both derived from it. So "Edit" is wired to an actual write
 * (`supabase.auth.updateUser`) rather than disabled — but only where that write
 * genuinely exists. Where it does not, the caller is handed a reason string to
 * show instead of a control that pretends.
 */

import { supabase } from '@/lib/supabase'
import { isCogoAuthProvider } from '@/services/auth-provider'
import { useStudioStore } from '@/store/studio-store'

export const PROFILE_SIGNED_OUT_REASON = 'Sign in to edit your profile'
export const PROFILE_PROVIDER_REASON =
  'This sign-in provider cannot update profiles yet'

/**
 * Why the display name cannot be edited right now, or `null` when it can be.
 *
 * The `cogo` identity provider (`VITE_AUTH_PROVIDER=cogo`) exposes
 * login/signup/refresh/logout/me only — see `services/auth-provider.ts`. There
 * is no profile-update endpoint behind it, so under that provider the control
 * must say so rather than call a Supabase client that is not the session's
 * identity source.
 */
export function displayNameEditBlockedReason(signedIn: boolean): string | null {
  if (!signedIn) return PROFILE_SIGNED_OUT_REASON
  if (isCogoAuthProvider) return PROFILE_PROVIDER_REASON
  return null
}

export type UpdateDisplayNameResult =
  | { ok: true; fullName: string }
  | { ok: false; error: string }

/**
 * Write `full_name` into the signed-in user's metadata and reflect the result
 * in the store, so the page shows the value the server accepted — not the value
 * that was typed. A rejected write surfaces its error instead of a fake
 * "Saved".
 */
export async function updateStudioDisplayName(
  fullName: string,
): Promise<UpdateDisplayNameResult> {
  const trimmed = fullName.trim()
  if (!trimmed) return { ok: false, error: 'Name cannot be empty' }

  const blocked = displayNameEditBlockedReason(true)
  if (blocked) return { ok: false, error: blocked }

  try {
    const { data, error } = await supabase.auth.updateUser({
      data: { full_name: trimmed },
    })
    if (error) return { ok: false, error: error.message }
    if (data?.user) useStudioStore.setState({ user: data.user })
    const saved =
      (data?.user?.user_metadata?.full_name as string | undefined) ?? trimmed
    return { ok: true, fullName: saved }
  } catch (cause) {
    return {
      ok: false,
      error: cause instanceof Error ? cause.message : 'Profile update failed',
    }
  }
}
